(function () {
  const el = document.querySelector('[data-pdp-shipping-eta]');
  if (!el) return;

  const num = (v, fallback) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const cfg = {
    // One of: instock | delayed | out | preorder. Picks the
    // template in render(); recomputed on variant change via deriveState().
    state: el.dataset.stockState || 'instock',
    preorder: el.dataset.preorder === 'true',
    cutoffHour: num(el.dataset.cutoffHour, 14),
    cutoffMinute: num(el.dataset.cutoffMinute, 0),
    timezone: el.dataset.timezone || 'Europe/Berlin',
    // Transit windows are CALENDAR days (couriers move on weekends too).
    // `de` is its own region since 30.07. Before that split Germany shared the
    // `eu` bucket, so it could not be quoted separately from Austria or France
    // — and domestic DHL really is 1-3 days, the shop's strongest argument at
    // home. Europe sits at 3-7 rather than the old 2-4: the bucket spans
    // Austria to Cyprus and carries the customs countries GB, CH, NO and IS,
    // for which 2-4 was never true. Overseas stays at the conservative 7-14
    // that the rate names promise at checkout.
    ranges: {
      de: [num(el.dataset.deMin, 1), num(el.dataset.deMax, 3)],
      eu: [num(el.dataset.euMin, 3), num(el.dataset.euMax, 7)],
      us: [num(el.dataset.usMin, 7), num(el.dataset.usMax, 14)],
      row: [num(el.dataset.rowMin, 7), num(el.dataset.rowMax, 14)],
    },
  };

  // Store locale (from request.locale.iso_code) drives ALL date formatting so
  // the weekday/month words match the storefront language instead of the
  // visitor's browser locale — no more "ships today, arrives Mi., 3. Juni" mix.
  const LOCALE = el.dataset.locale || undefined;

  // Sentence templates come from the locale files via data-attributes, so the
  // wording is translatable. {countdown}/{day}/{range} are filled client-side.
  const i18n = {
    inStockToday: el.dataset.i18nInStockToday || 'Order in the next {countdown} · ships {day}, arrives {range}',
    inStockFuture: el.dataset.i18nInStockFuture || 'Ships {day}, arrives {range}',
    today: el.dataset.i18nToday || 'today',
    tomorrow: el.dataset.i18nTomorrow || 'tomorrow',
    // Static states (no live date). Defaults mirror the Liquid fallback text.
    delayed: el.dataset.i18nDelayed || 'Back in stock soon · ships as soon as it arrives · tracked worldwide.',
    out: el.dataset.i18nOut || 'Currently unavailable · no delivery estimate.',
    // No shipping-cost claim here, deliberately: this line states TIMING, and
    // the cost depends on the order total. It used to say "free EU shipping",
    // which was unconditional (free shipping starts at the threshold) and also
    // wrong about geography (the threshold applies in every zone, not just the
    // EU). Locale strings are out of reach of snippets/shipping-claim.liquid,
    // so a claim placed here can never be corrected at render time. Keep it
    // parallel to `delayed` and `out`, which name no price either.
    preorder: el.dataset.i18nPreorder || 'Pre-order · ships when ready · tracked worldwide.',
  };

  const sectionEl = el.closest('product-info');
  const mySectionId = sectionEl
    ? sectionEl.dataset.originalSection || sectionEl.dataset.section
    : null;

  const NA_PREFIXES = [
    'America/Adak', 'America/Anchorage', 'America/Boise', 'America/Chicago',
    'America/Denver', 'America/Detroit', 'America/Edmonton', 'America/Halifax',
    'America/Indiana', 'America/Juneau', 'America/Kentucky', 'America/Los_Angeles',
    'America/Mexico_City', 'America/Montreal', 'America/New_York', 'America/Nome',
    'America/Phoenix', 'America/Regina', 'America/Toronto', 'America/Vancouver',
    'America/Whitehorse', 'America/Winnipeg', 'America/Yellowknife',
  ];

  function detectRegion() {
    // Server-side region (Shopify Markets country resolution) is authoritative.
    // Browser-timezone is only a fallback for the rare case it is absent.
    const fromServer = el.dataset.region;
    if (fromServer === 'de' || fromServer === 'eu' || fromServer === 'us' || fromServer === 'row') return fromServer;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      // Germany first: Europe/Berlin (and the historical Europe/Busingen) are
      // German-only zones, so this fallback cannot misfire on another country.
      if (tz === 'Europe/Berlin' || tz === 'Europe/Busingen') return 'de';
      if (tz.startsWith('Europe/') || tz.startsWith('Atlantic/Canary') || tz.startsWith('Atlantic/Madeira')) return 'eu';
      if (NA_PREFIXES.some((p) => tz.startsWith(p))) return 'us';
      return 'row';
    } catch (e) {
      return 'row';
    }
  }

  const region = detectRegion();
  const [shipMin, shipMax] = cfg.ranges[region];

  const DAY_MS = 86400000;

  // ----- Cached Intl formatters (creating them is relatively expensive and
  //       render() runs every minute). All format UTC-midnight dates that
  //       stand in for the merchant's calendar day, so timeZone MUST be UTC. -----
  const merchantClockFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: cfg.timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const weekdayLongFmt = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' });
  const arrivalFmt = new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });

  function nowInMerchantTZ() {
    const parts = {};
    for (const p of merchantClockFmt.formatToParts(new Date())) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    }
    return {
      year: +parts.year,
      month: +parts.month,
      day: +parts.day,
      hour: +parts.hour % 24,
      minute: +parts.minute,
      second: +parts.second,
    };
  }

  // ----- Public holidays: the 9 Germany-wide statutory holidays, plus the
  //       three Bavarian ones for DISPATCH (the workshop is in Bavaria and
  //       does not ship on them). Delivery clamping keeps the nationwide set:
  //       couriers outside Bavaria deliver on those days. The movable ones
  //       derive from Easter (Anonymous Gregorian / Meeus algorithm), so this
  //       needs no yearly maintenance. Cached per year. -----
  const holidayCache = {};
  const bavarianCache = {};

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-based: 3 = March, 4 = April
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return Date.UTC(year, month - 1, day);
  }

  function ymdKey(dateUTC) {
    const y = dateUTC.getUTCFullYear();
    const m = String(dateUTC.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dateUTC.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function holidaysForYear(year) {
    if (holidayCache[year]) return holidayCache[year];
    const set = new Set();
    const addFixed = (mon, day) => set.add(`${year}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    // Fixed-date nationwide holidays.
    addFixed(1, 1);   // Neujahr
    addFixed(5, 1);   // Tag der Arbeit
    addFixed(10, 3);  // Tag der Deutschen Einheit
    addFixed(12, 25); // 1. Weihnachtstag
    addFixed(12, 26); // 2. Weihnachtstag
    // Easter-derived nationwide holidays.
    const easter = easterSunday(year);
    const addOffset = (days) => set.add(ymdKey(new Date(easter + days * DAY_MS)));
    addOffset(-2); // Karfreitag
    addOffset(1);  // Ostermontag
    addOffset(39); // Christi Himmelfahrt
    addOffset(50); // Pfingstmontag
    holidayCache[year] = set;
    return set;
  }

  // Nationwide set + Heilige Drei Könige, Fronleichnam, Allerheiligen.
  function workshopHolidaysForYear(year) {
    if (bavarianCache[year]) return bavarianCache[year];
    const set = new Set(holidaysForYear(year));
    set.add(`${year}-01-06`); // Heilige Drei Könige
    set.add(`${year}-11-01`); // Allerheiligen
    set.add(ymdKey(new Date(easterSunday(year) + 60 * DAY_MS))); // Fronleichnam
    bavarianCache[year] = set;
    return set;
  }

  function isWorkingDay(dateUTC) {
    const wd = dateUTC.getUTCDay();
    if (wd === 0 || wd === 6) return false; // Sun / Sat
    return !workshopHolidaysForYear(dateUTC.getUTCFullYear()).has(ymdKey(dateUTC));
  }

  function merchantTodayUTC() {
    const n = nowInMerchantTZ();
    return new Date(Date.UTC(n.year, n.month - 1, n.day));
  }

  function addWorkingDays(dateUTC, n) {
    const d = new Date(dateUTC.getTime());
    let remaining = n;
    while (remaining > 0) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (isWorkingDay(d)) remaining -= 1;
    }
    return d;
  }

  function nextWorkingDay(dateUTC) {
    const d = new Date(dateUTC.getTime());
    do {
      d.setUTCDate(d.getUTCDate() + 1);
    } while (!isWorkingDay(d));
    return d;
  }

  // Couriers in DE deliver Mon–Sat but not Sundays or public holidays. Transit
  // is counted in calendar days, so a computed arrival can land on a non-
  // delivery day — nudge it to the next day a parcel could actually arrive.
  function clampToDeliveryDay(dateUTC) {
    const d = new Date(dateUTC.getTime());
    while (d.getUTCDay() === 0 || holidaysForYear(d.getUTCFullYear()).has(ymdKey(d))) {
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return d;
  }

  function timeUntilNextCutoffSec() {
    const now = nowInMerchantTZ();
    const todayUTC = merchantTodayUTC();
    const cutoffSec = cfg.cutoffHour * 3600 + cfg.cutoffMinute * 60;
    const nowSec = now.hour * 3600 + now.minute * 60 + now.second;

    if (isWorkingDay(todayUTC) && nowSec < cutoffSec) {
      return cutoffSec - nowSec;
    }
    // Roll to the cutoff on the next working day: rest of today + any whole
    // non-working days in between + the cutoff time itself.
    let remaining = 24 * 3600 - nowSec;
    const probe = new Date(todayUTC.getTime());
    probe.setUTCDate(probe.getUTCDate() + 1);
    while (!isWorkingDay(probe)) {
      remaining += 24 * 3600;
      probe.setUTCDate(probe.getUTCDate() + 1);
    }
    return remaining + cutoffSec;
  }

  function formatDuration(totalSec) {
    if (totalSec < 0) totalSec = 0;
    const totalMin = Math.floor(totalSec / 60);
    const days = Math.floor(totalMin / (24 * 60));
    const hours = Math.floor((totalMin % (24 * 60)) / 60);
    const minutes = totalMin % 60;
    if (days >= 1) return `${days}d ${hours}h`;
    if (hours >= 1) return `${hours}h ${minutes}m`;
    return `${Math.max(1, minutes)}m`;
  }

  // The dispatch day = the working day the parcel leaves us. In stock: today
  // (before cutoff on a working day) or the next working day. For made-to-order
  // production is added on top in arrivalRange().
  function shipContext() {
    const now = nowInMerchantTZ();
    const todayUTC = merchantTodayUTC();
    const todayIsWorking = isWorkingDay(todayUTC);
    const pastCutoff = now.hour * 60 + now.minute >= cfg.cutoffHour * 60 + cfg.cutoffMinute;
    const shipsToday = todayIsWorking && !pastCutoff;
    const shipUTC = shipsToday ? todayUTC : nextWorkingDay(todayUTC);
    return { shipsToday, todayUTC, shipUTC };
  }

  function shipDayLabel(ctx) {
    if (ctx.shipsToday) return i18n.today;
    const diffDays = Math.round((ctx.shipUTC.getTime() - ctx.todayUTC.getTime()) / DAY_MS);
    if (diffDays === 1) return i18n.tomorrow;
    return weekdayLongFmt.format(ctx.shipUTC);
  }

  // Mirror of the Liquid eta_state logic, from the variant-change payload.
  // available separates delayed (continue selling) from out (deny); untracked
  // variants count as in stock.
  function deriveState(v) {
    if (!v) return 'out';
    if (cfg.preorder && v.available) return 'preorder';
    if (!v.available) return 'out';
    // Stock-based shop (no make-on-demand): untracked = in stock / normal
    // dispatch. Only a tracked variant at 0 with continue-selling is delayed.
    if (v.inventory_management !== 'shopify' || v.inventory_quantity > 0) return 'instock';
    return 'delayed';
  }

  function arrivalRange(ctx) {
    const todayUTC = ctx.todayUTC;
    // Working days until dispatch: 0 if it ships today, else 1 (next working
    // day). addWorkingDays already skips weekends + holidays, so we must NOT
    // add a calendar-day gap here (that double-counted weekends before).
    //
    // There is no production window any more. It only ever applied to a
    // `production` state that neither the Liquid nor this file ever assigned,
    // so the setting and the per-product metafields were inert; removed 29.07.
    // The shop is stock-based: dispatch is same or next working day.
    const baseOffset = ctx.shipsToday ? 0 : 1;

    // Dispatch counts working days; transit counts calendar days.
    const dispatch = addWorkingDays(todayUTC, baseOffset);
    const earliest = clampToDeliveryDay(new Date(dispatch.getTime() + shipMin * DAY_MS));
    const latest = clampToDeliveryDay(new Date(dispatch.getTime() + shipMax * DAY_MS));

    return {
      start: arrivalFmt.format(earliest),
      end: arrivalFmt.format(latest),
      sameDay: earliest.getTime() === latest.getTime(),
    };
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  const strong = (s) => `<strong>${escapeHTML(s)}</strong>`;

  function interpolate(tpl, vars) {
    return tpl.replace(/\{(\w+)\}/g, (_, key) => (key in vars ? vars[key] : ''));
  }

  function render() {
    // Stop cleanly if the element was ever removed (e.g. a full product swap).
    if (!el.isConnected) {
      if (timer) clearInterval(timer);
      return;
    }

    // Tint the surrounding box to match the stock badge.
    const box = el.closest('.pdp-ship-est');
    if (box) {
      box.classList.remove('pdp-ship-est--delayed', 'pdp-ship-est--out', 'pdp-ship-est--preorder');
      if (cfg.state === 'delayed' || cfg.state === 'out' || cfg.state === 'preorder') {
        box.classList.add('pdp-ship-est--' + cfg.state);
      }
    }

    // Static states carry no live countdown / arrival date.
    if (cfg.state === 'delayed') { el.textContent = i18n.delayed; return; }
    if (cfg.state === 'out') { el.textContent = i18n.out; return; }
    if (cfg.state === 'preorder') { el.textContent = i18n.preorder; return; }

    const ctx = shipContext();
    const arr = arrivalRange(ctx);
    const rangeText = arr.sameDay ? arr.start : `${arr.start} – ${arr.end}`;
    const vars = {
      countdown: strong(formatDuration(timeUntilNextCutoffSec())),
      day: strong(shipDayLabel(ctx)),
      range: strong(rangeText),
    };

    // Only `instock` renders a live date. The other three states (delayed,
    // out, preorder) are static sentences handled before this point.
    const tpl = ctx.shipsToday ? i18n.inStockToday : i18n.inStockFuture;
    el.innerHTML = interpolate(tpl, vars);
  }

  // Re-render when the shopper switches variant: Dawn does not touch this
  // element, but it publishes the new variant. Recompute the full state
  // (instock / delayed / out / preorder) and re-render.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const data = payload && payload.data;
      if (!data || (mySectionId && data.sectionId && data.sectionId !== mySectionId)) return;
      if (!el.isConnected) {
        unsubscribe();
        return;
      }
      cfg.state = deriveState(data.variant);
      render();
    });
  }

  render();
  var timer = setInterval(render, 60000);
})();
